import { AppDispatch, RootState } from "@Reducers/index";
import { getUrlForImage } from "@CubeComponents/constants";
import axiosWrapper, { postTo } from "@AxiosWrapper";
import { QueryBuilder, executeQuery } from "../services/queryBuilder";
import { setProjectData, setSpaceData } from "../reducers/breadcrumbsdata";
import { getCurrencyData } from "./Currency";
import type { GetSpaceByIdRequest } from "@ServerTypes/spaces/space.schema";
import type {
  GetIncludedProjectsControllerRequest,
  GetProjectByIdRequest,
  IncludedProjectItem,
} from "@ServerTypes/projects/project.schema";
import type { Exact } from "@Helpers/types/exact";
import { toObjectIdString } from "@Helpers/schemas/common.schema";

/** `getIncludedProjectsController`'s scope selector, typed from its schema. */
type ProjectScope = NonNullable<GetIncludedProjectsControllerRequest["value"]>["originSource"];

/**
 * `id` is the project's Mongo _id, exactly as `/getProjectById/:id` names it — but the fetch below goes to
 * `getIncludedProjectsController` (a `_id equal` filter), not to `/getProjectById`, so `originSource` is
 * THAT route's scope selector and is typed from its schema. `/getProjectById` itself never read an
 * `originSource` and no longer declares one.
 */
type CurrProjectDataRequest = GetProjectByIdRequest & {
  originSource?: ProjectScope;
};

/** One project's `/getIncludedProjectsController` row; `undefined` when the read is refused or matches nothing. */
async function readProjectRow(id: string, originSource: ProjectScope) {
  const queryResult = await executeQuery(postTo("/projects/getIncludedProjectsController"), {
    ...new QueryBuilder().where("_id", "equal", id).build(),
    ...(originSource ? { originSource } : {}),
  });
  return queryResult.status ? queryResult.result[0] : undefined;
}

/** Stores a project row in the slice, with the cover-image URL when the row carries an image key. */
function storeProjectRow(dispatch: AppDispatch, row: IncludedProjectItem, id: string) {
  if (row.image) {
    dispatch(setProjectData({ ...row, projectImageUrl: getUrlForImage({ id }, "projectImage") }));
  } else {
    dispatch(setProjectData({ ...row }));
  }
}

/**
 * The number of the last `getCurrProjectData` read started. The slice holds one project and the Router renders a
 * `?projectId` route only once it holds THAT project (`utils/projectGate.ts`), so a slower read of the previous
 * project landing after the current one would leave the route loading forever: only the latest read is stored.
 */
let latestProjectRead = 0;

export function getCurrProjectData<T extends Exact<CurrProjectDataRequest, T>>(data: T) {
  return async (dispatch: AppDispatch) => {
    // data.id is already validated (Exact<GetProjectByIdRequest>), but the Exact<> generic widens it
    // to a union here; toObjectIdString re-tags it without re-validating (runtime no-op, as String() was).
    const id = toObjectIdString(data.id);
    const read = ++latestProjectRead;
    const row = await readProjectRow(id, data.originSource);
    // Expected and harmless: a newer read (a project switch) started meanwhile and stores its own project and
    // currency; this one's result is for a route no longer shown.
    if (read !== latestProjectRead) return;
    // A read that finds nothing clears the slice: that is the not-found / no-permission state the router
    // renders as Forbidden.
    storeProjectRow(dispatch, row ?? {}, id);
    // Fire-and-forget thunk: the currency lands in the store on its own schedule.
    void dispatch(getCurrencyData({ projectId: id }));
  };
}

/**
 * Re-reads the open project after an edit to it was saved. Unlike `getCurrProjectData`, a read that finds
 * nothing leaves the slice as it was, so a failed refresh cannot turn a successful save into Forbidden.
 */
export function refreshCurrProjectData<T extends Exact<CurrProjectDataRequest, T>>(data: T) {
  return async (dispatch: AppDispatch) => {
    // Re-tagged for the same reason as in `getCurrProjectData`.
    const id = toObjectIdString(data.id);
    const read = latestProjectRead;
    const row = await readProjectRow(id, data.originSource);
    // Expected and harmless: a project switch started a newer read meanwhile, whose project the slice must hold.
    if (row && read === latestProjectRead) {
      storeProjectRow(dispatch, row, id);
    }
  };
}

export function getCurrSpaceData<T extends Exact<GetSpaceByIdRequest, T>>(data: T) {
  return (dispatch: AppDispatch) => {
    // Fire-and-forget: a thunk body cannot await. The dead `.catch` that used to sit at the end of
    // this chain only rebuilt and dropped the rejection; leaving it off keeps the real one
    // unhandled, which is exactly what Sentry's global handler reports.
    void axiosWrapper.api.post("/spaces/getSpaceById", data).then((res) => {
      // The not-found / no-permission branches answer without `data`; the slice holds the space
      // shape itself, so an empty one is stored rather than a marker field it cannot carry. Typed as
      // the slice's state because, left to inference, the `{}` arm widens the union to a bare `{}`.
      const spaceData: RootState["spaceData"] = res.data.status ? res.data.data : {};
      dispatch(setSpaceData(spaceData));
    });
  };
}
